// src/pages/accounting/collateralReceived/components/CollateralReceivedTable.jsx
import React from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';

const amountBody = (row) => {
    if (row?.enteredAmount == null) return '';
    const formatted = Number(row.enteredAmount).toLocaleString(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${formatted} ${row.currency ?? ''}`.trim();
};

/**
 * Alınan Teminatlar listesi. Backend `id` azalan sabit sırayla döner ve sıralama/filtre
 * parametresi sunmaz (bkz. docs/CollateralReceived-API.md §3.1) — bu yüzden kolonlar
 * `sortable` değildir ve satır bazlı filtre yerine üstteki `CollateralReceivedFilter`
 * (gelişmiş filtre paneli) kullanılır; var olmayan bir yeteneği taklit etmek yerine
 * gerçek API sözleşmesine sadık kalındı.
 */
const CollateralReceivedTable = ({ th, data, totalRecords, pagination, onPage, loading, onEdit, onDelete, canBrowseAllDealers }) => {
    const actionBodyTemplate = (row) => (
        <div className="flex align-items-center justify-content-center gap-3">
            <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" onClick={() => onEdit(row)} title={th('common.edit')} />
            <i className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700" onClick={() => onDelete(row)} title={th('common.delete')} />
        </div>
    );

    const columns = [
        { field: '__actions', header: th('common.actions'), body: actionBodyTemplate, lockedFromPrefs: true, lockedPosition: 'start', style: { width: '5%', minWidth: '80px', textAlign: 'center' } },
        ...(canBrowseAllDealers ? [{ field: 'sapDealerId', header: th('collateralReceived.sapDealerId'), style: { minWidth: '110px' } }] : []),
        ...(canBrowseAllDealers ? [{ field: 'dealerName', header: th('collateralReceived.dealerName'), body: (row) => createTruncatedTextForTable(row.dealerName, 40), style: { minWidth: '220px' } }] : []),
        { field: 'numbers', header: th('collateralReceived.numbers'), style: { minWidth: '140px' } },
        { field: 'typeName', header: th('collateralReceived.type'), style: { minWidth: '150px' } },
        { field: 'purchaseDate', header: th('collateralReceived.purchaseDate'), body: (row) => dateOnlyBodyTemplate(row, 'purchaseDate'), style: { minWidth: '120px' } },
        { field: 'expiryDate', header: th('collateralReceived.expiryDate'), body: (row) => dateOnlyBodyTemplate(row, 'expiryDate'), style: { minWidth: '120px' } },
        { field: 'extensionDate', header: th('collateralReceived.extensionDate'), body: (row) => dateOnlyBodyTemplate(row, 'extensionDate'), style: { minWidth: '120px' } },
        { field: 'isReturned', header: th('collateralReceived.isReturned'), body: (row) => (row.isReturned === '1' ? th('common.yes') : th('common.no')), style: { minWidth: '110px' } },
        { field: 'returnDate', header: th('collateralReceived.returnDate'), body: (row) => dateOnlyBodyTemplate(row, 'returnDate'), style: { minWidth: '120px' } },
        { field: 'enteredAmount', header: th('collateralReceived.enteredAmount'), body: amountBody, style: { minWidth: '140px', textAlign: 'right' } },
        { field: 'validity', header: th('collateralReceived.validity'), style: { minWidth: '100px' } },
        { field: 'bankName', header: th('collateralReceived.bankName'), style: { minWidth: '180px' } },
        { field: 'bankBranchName', header: th('collateralReceived.bankBranchName'), body: (row) => createTruncatedTextForTable(row.bankBranchName, 40), style: { minWidth: '160px' } },
        { field: 'reason', header: th('collateralReceived.reason'), body: (row) => createTruncatedTextForTable(row.reason, 40), style: { minWidth: '200px' } },
        { field: 'createdName', header: th('collateralReceived.createdName'), style: { minWidth: '160px' } },
        { field: 'createdDate', header: th('collateralReceived.createdDate'), body: (row) => dateOnlyBodyTemplate(row, 'createdDate'), style: { minWidth: '140px' } }
    ];

    return (
        <>
            <ConfirmDialog />
            <PreferencesDataTable
                tableKey="collateral-received"
                columns={columns}
                value={data}
                dataKey="id"
                lazy
                paginator
                first={(pagination.pageNumber - 1) * pagination.pageSize}
                rows={pagination.pageSize}
                totalRecords={totalRecords}
                onPage={onPage}
                loading={loading}
                showGridlines
                rowsPerPageOptions={[10, 25, 50]}
                tableStyle={{ minWidth: '70rem' }}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={th('table.paginatorTemplate')}
                emptyMessage={th('common.noRecords')}
            />
        </>
    );
};

export default CollateralReceivedTable;
